import { Info, Question } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import styled from 'styled-components';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getLiveCatalogCurrentItemId,
    getLiveItemDataCurrentAskPrice,
    getLiveItemDataLeadingBidder,
} from '@/redux/modules/console';
import { getItemSummaryCurrency, getItemSummaryStartPrice } from '@/redux/modules/item/summary/itemSummary.selectors';
import { openBidLimitExceededModal, openWaitingForApprovalModal } from '@/redux/modules/modal';

import BiddingConsoleButton from '../../../BiddingConsoleButton/BiddingConsoleButton';

type Props = {
    biddingLocked: boolean;
    catalogId: number;
    mobile?: boolean;
    toggleBiddingLocked: () => void;
};

const ButtonGroup = ({ biddingLocked, catalogId, mobile = false, toggleBiddingLocked }: Props) => {
    const dispatch = useAppDispatch();

    const catalogRegistration = useAppSelector((state) => getCatalogRegistration(state, catalogId));
    const itemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const currentItemStartPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));
    const currentItemCurrency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));
    const leadingBidder = useAppSelector((state) => getLiveItemDataLeadingBidder(state, itemId));
    const bidderId = useAppSelector((state) => getBidderId(state));

    const ask = currentAskPrice || currentItemStartPrice;
    const overBidLimit = ask > bidLimitBalance;
    const winning = bidderId === leadingBidder;

    if (catalogRegistration.pending) {
        return (
            <>
                <BiddingConsoleButton
                    biddingLocked={biddingLocked}
                    catalogId={catalogId}
                    itemId={itemId}
                    showToggleLockedSwitch={mobile}
                    toggleBiddingLocked={toggleBiddingLocked}
                />
                <SplitButton onClick={() => dispatch(openWaitingForApprovalModal(catalogId))}>
                    <FormattedMessage id="bidding_console.console.button.waiting" />
                    &nbsp;
                    <Question
                        className="inline-block"
                        size="sm"
                        style="fill"
                    />
                </SplitButton>
            </>
        );
    }

    if (catalogRegistration.limited) {
        if (overBidLimit && !winning) {
            return (
                <>
                    <BiddingConsoleButton
                        biddingLocked={biddingLocked}
                        catalogId={catalogId}
                        itemId={itemId}
                        showToggleLockedSwitch={mobile}
                        toggleBiddingLocked={toggleBiddingLocked}
                    />
                    <SplitButton onClick={() => dispatch(openBidLimitExceededModal(catalogId))}>
                        <LimitExceededCurrencyContainer>
                            <FormattedMessage
                                id="bidding_console.console.button.limit_exceeded"
                                values={{
                                    limit: (
                                        <FormattedCurrency
                                            key="button-group-limit-exceeded-limit"
                                            noFraction
                                            sourceCurrency={currentItemCurrency}
                                            value={catalogRegistration.bidLimit}
                                        />
                                    ),
                                }}
                            />
                        </LimitExceededCurrencyContainer>
                        &nbsp;
                        <Info
                            className="inline-block"
                            size="sm"
                            style="fill"
                        />
                    </SplitButton>
                </>
            );
        }

        if (!overBidLimit && !winning) {
            return (
                <>
                    <BiddingConsoleButton
                        biddingLocked={biddingLocked}
                        catalogId={catalogId}
                        itemId={itemId}
                        showToggleLockedSwitch={mobile}
                        toggleBiddingLocked={toggleBiddingLocked}
                    />
                    <SplitButton>
                        <FormattedMessage
                            id="bidding_console.console.button.approved_limited"
                            values={{
                                balance: (
                                    <ApprovedLimitedCurrencyContainer key="approved-limited-balance">
                                        <FormattedCurrency
                                            noFraction
                                            sourceCurrency={currentItemCurrency}
                                            value={bidLimitBalance}
                                        />
                                    </ApprovedLimitedCurrencyContainer>
                                ),
                                limit: (
                                    <ApprovedLimitedCurrencyContainer key="approved-limited-limit">
                                        <FormattedCurrency
                                            noFraction
                                            sourceCurrency={currentItemCurrency}
                                            value={catalogRegistration.bidLimit}
                                        />
                                    </ApprovedLimitedCurrencyContainer>
                                ),
                            }}
                        />
                    </SplitButton>
                </>
            );
        }
    }

    return (
        <BiddingConsoleButton
            biddingLocked={biddingLocked}
            catalogId={catalogId}
            itemId={itemId}
            showToggleLockedSwitch={mobile}
            toggleBiddingLocked={toggleBiddingLocked}
        />
    );
};

export default ButtonGroup;

const LimitExceededCurrencyContainer = styled.span`
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
`;

const ApprovedLimitedCurrencyContainer = styled.span`
    display: contents;
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
`;

const SplitButton = styled.div.attrs({ role: 'button' })`
    align-items: center;
    color: ${({ theme }) => theme.colors.grey100};
    cursor: pointer;
    display: block;
    flex: 0 1 auto;
    font-size: 16px;
    margin: 10px 0 0 10px;
    text-align: left;

    svg {
        color: ${({ theme }) => theme.colors.grey300};
    }
`;
